// File: frontend/src/pages/BlogPage.js
// Version: 1.0 (2026-10-24, Part A)
// Description: Раздел «Блог» в OS ELN (estatelawnewyork.com).
// Вкладки:
//  1. Вышло (Published): вышедшие статьи, фильтр 7/30/все дни, прямая ссылка, статус проверки
//  2. В очереди (In Queue): ожидающие публикации статьи, прогноз по датам
//  3. Темы (Topic Suggestions): рекомендованные темы, фильтр по статусу, форма добавления

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    Clock,
    ExternalLink,
    HelpCircle,
    Mail,
    Plus,
    ShieldAlert,
    ShieldCheck,
    Sparkles,
    XCircle,
} from 'lucide-react';
import apiService from '../services/apiService';
import { useUiLanguage } from '../context/UiLanguageContext';
import {
    Badge,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    EmptyState,
    Input,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Textarea,
} from '../components/ui';

export function isCanonicalBlogUrl(url, slug) {
    if (!url || typeof url !== 'string') return false;
    const cleanSlug = (slug || '').trim();
    if (!cleanSlug) return false;
    return url === `https://estatelawnewyork.com/blog/${cleanSlug}/` || url === `https://estatelawnewyork.com/blog/${cleanSlug}`;
}

export function formatCycleMonth(cycle, lang) {
    if (!cycle || typeof cycle !== 'string') return null;
    const parts = cycle.split('-');
    if (parts.length !== 2) return cycle;
    const [year, month] = parts;
    const mNum = parseInt(month, 10);
    if (isNaN(mNum) || mNum < 1 || mNum > 12) return cycle;

    if (lang === 'ru') {
        const ruMonths = [
            'янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июн.',
            'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.',
        ];
        return `${ruMonths[mNum - 1]} ${year}`;
    }
    const enMonths = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    return `${enMonths[mNum - 1]} ${year}`;
}

export function getQualityStatus(quality) {
    if (!quality) {
        return { status: 'none', passed: 0, failed: 0, notMeasured: 0, total: 0, failedChecks: [], notMeasuredChecks: [], checks: {} };
    }
    const checks = quality.checks || quality;
    if (!checks || typeof checks !== 'object') {
        return { status: 'none', passed: 0, failed: 0, notMeasured: 0, total: 0, failedChecks: [], notMeasuredChecks: [], checks: {} };
    }
    const entries = Object.entries(checks).filter(
        ([k, v]) => typeof v === 'object' && v !== null && ('passed' in v || 'status' in v)
    );
    if (entries.length === 0) {
        return { status: 'none', passed: 0, failed: 0, notMeasured: 0, total: 0, failedChecks: [], notMeasuredChecks: [], checks: {} };
    }
    const failedChecks = entries
        .filter(([, v]) => v.status === 'fail' || v.passed === false)
        .map(([k]) => k);
    const notMeasuredChecks = entries
        .filter(([, v]) => v.status === 'not_measured' || (v.passed === null && v.status !== 'fail' && v.status !== 'pass'))
        .map(([k]) => k);
    const total = entries.length;
    const passed = total - failedChecks.length - notMeasuredChecks.length;

    let status = 'pass';
    if (failedChecks.length > 0) {
        status = 'fail';
    } else if (notMeasuredChecks.length > 0) {
        status = 'partial';
    }

    return {
        status,
        passed,
        failed: failedChecks.length,
        notMeasured: notMeasuredChecks.length,
        total,
        failedChecks,
        notMeasuredChecks,
        checks,
    };
}

const SUGGESTION_STATUS_ORDER = {
    accepted: 1,
    in_queue: 2,
    proposed: 3,
    published: 4,
    rejected: 5,
};

const SUGGESTION_PRIORITY_ORDER = {
    high: 1,
    normal: 2,
    low: 3,
};

export default function BlogPage() {
    const { t, lang } = useUiLanguage();

    const [activeTab, setActiveTab] = useState('published');
    const [overviewData, setOverviewData] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(true);

    // Filter for published tab: '7d' | '30d' | 'all'
    const [publishedFilter, setPublishedFilter] = useState('7d');

    // Suggestions state
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [suggestionStatusFilter, setSuggestionStatusFilter] = useState('all');
    const [suggestionKindFilter, setSuggestionKindFilter] = useState('all');

    // Selected article for Details Card Modal
    const [selectedArticle, setSelectedArticle] = useState(null);

    // Modal state for proposing a topic
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [submittingModal, setSubmittingModal] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        language: 'en',
        keyword: '',
        priority: 'normal',
        category: '',
        kind: 'new',
        channel: 'blog',
        notes: '',
        queue_id: '',
    });

    // Load overview data
    const loadOverview = useCallback(async () => {
        try {
            setLoadingOverview(true);
            const data = await apiService.getBlogOverview();
            setOverviewData(data);
        } catch (err) {
            console.error('Error loading blog overview', err);
        } finally {
            setLoadingOverview(false);
        }
    }, []);

    // Load suggestions
    const loadSuggestions = useCallback(async () => {
        try {
            setLoadingSuggestions(true);
            const items = await apiService.getBlogSuggestions('all');
            setSuggestions(Array.isArray(items) ? items : []);
        } catch (err) {
            console.error('Failed to load blog suggestions', err);
        } finally {
            setLoadingSuggestions(false);
        }
    }, []);

    useEffect(() => {
        loadOverview();
    }, [loadOverview]);

    useEffect(() => {
        if (activeTab === 'suggestions') {
            loadSuggestions();
        }
    }, [activeTab, loadSuggestions]);

    // Counters for suggestions: Proposed N · Accepted N · In queue N · Published N
    const suggestionCounters = useMemo(() => {
        const counts = { proposed: 0, accepted: 0, in_queue: 0, published: 0 };
        (suggestions || []).forEach((s) => {
            if (counts[s.status] !== undefined) {
                counts[s.status]++;
            }
        });
        return counts;
    }, [suggestions]);

    // Katifi cycle statistics from loaded suggestions: "Katifi cycle Oct 2026: 3 rewrite · 8 new"
    const cycleSummary = useMemo(() => {
        const cycleList = (suggestions || [])
            .map((s) => s.cycle)
            .filter(Boolean)
            .sort()
            .reverse();
        if (cycleList.length === 0) return null;
        const latestCycle = cycleList[0];
        const cycleTopics = (suggestions || []).filter((s) => s.cycle === latestCycle);
        const rewrites = cycleTopics.filter((s) => s.kind === 'rewrite').length;
        const news = cycleTopics.filter((s) => (s.kind || 'new') === 'new').length;
        const formattedCycle = formatCycleMonth(latestCycle, lang);
        return {
            cycle: formattedCycle,
            rawCycle: latestCycle,
            rewrites,
            news,
            text: t('blog.suggestions.cycle_summary', {
                cycle: formattedCycle,
                rewrites,
                news,
            }),
        };
    }, [suggestions, lang, t]);

    // Filter and sort suggestions: accepted -> in_queue -> proposed -> published -> rejected
    const sortedSuggestions = useMemo(() => {
        let list = suggestions || [];
        if (suggestionStatusFilter && suggestionStatusFilter !== 'all') {
            list = list.filter((s) => s.status === suggestionStatusFilter);
        }
        if (suggestionKindFilter && suggestionKindFilter !== 'all') {
            list = list.filter((s) => (s.kind || 'new') === suggestionKindFilter);
        }
        return [...list].sort((a, b) => {
            const orderA = SUGGESTION_STATUS_ORDER[a.status] ?? 99;
            const orderB = SUGGESTION_STATUS_ORDER[b.status] ?? 99;
            if (orderA !== orderB) return orderA - orderB;

            const prioA = SUGGESTION_PRIORITY_ORDER[a.priority] ?? 99;
            const prioB = SUGGESTION_PRIORITY_ORDER[b.priority] ?? 99;
            if (prioA !== prioB) return prioA - prioB;

            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            if (dateA !== dateB) return dateB - dateA;

            return (b.id || 0) - (a.id || 0);
        });
    }, [suggestions, suggestionStatusFilter, suggestionKindFilter]);

    const snapshot = overviewData?.snapshot || null;
    const isStale = !!overviewData?.stale;
    const receivedAt = overviewData?.received_at || null;

    // Filter published articles
    const publishedArticles = useMemo(() => {
        const list = snapshot?.published || [];
        if (publishedFilter === 'all') return list;

        const now = new Date();
        const daysLimit = publishedFilter === '7d' ? 7 : 30;
        const cutoff = new Date(now.getTime() - daysLimit * 24 * 60 * 60 * 1000);

        return list.filter((item) => {
            if (!item.date) return true;
            const itemDate = new Date(item.date);
            return isNaN(itemDate.getTime()) ? true : itemDate >= cutoff;
        });
    }, [snapshot?.published, publishedFilter]);

    // Count for 7 days
    const count7d = useMemo(() => {
        const list = snapshot?.published || [];
        const now = new Date();
        const cutoff7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return list.filter((item) => {
            if (!item.date) return false;
            const itemDate = new Date(item.date);
            return !isNaN(itemDate.getTime()) && itemDate >= cutoff7d;
        }).length;
    }, [snapshot?.published]);

    // Pending articles
    const pendingArticles = useMemo(() => {
        return snapshot?.pending || [];
    }, [snapshot?.pending]);

    // Latest expected date for pending banner
    const lastExpectedDate = useMemo(() => {
        if (!pendingArticles.length) return null;
        const sorted = [...pendingArticles].sort((a, b) => (a.expected_date || '').localeCompare(b.expected_date || ''));
        return sorted[sorted.length - 1]?.expected_date || null;
    }, [pendingArticles]);

    // Suggestion actions
    const handleStatusChange = async (id, newStatus) => {
        try {
            await apiService.updateBlogSuggestion(id, { status: newStatus });
            loadSuggestions();
        } catch (err) {
            console.error('Failed to update topic status', err);
        }
    };

    // Modal submit
    const handleSubmitSuggestion = async (e) => {
        if (e) e.preventDefault();
        if (!formData.title.trim()) return;

        try {
            setSubmittingModal(true);
            setModalError(null);
            await apiService.createBlogSuggestion({
                title: formData.title.trim(),
                language: formData.language,
                keyword: formData.keyword.trim() || undefined,
                priority: formData.priority,
                category: formData.category?.trim() || undefined,
                kind: formData.kind || 'new',
                channel: formData.channel || 'blog',
                proposed_by: 'staff',
                notes: formData.notes.trim() || undefined,
                queue_id: formData.queue_id.trim() || undefined,
            });
            setIsModalOpen(false);
            setFormData({
                title: '',
                language: 'en',
                keyword: '',
                priority: 'normal',
                category: '',
                kind: 'new',
                channel: 'blog',
                notes: '',
                queue_id: '',
            });
            loadSuggestions();
        } catch (err) {
            setModalError(err.message || t('blog.modal.error'));
        } finally {
            setSubmittingModal(false);
        }
    };

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="blog-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-border pb-4">
                <div>
                    <PageHeader
                        title={t('blog.header.title')}
                        description={t('blog.header.subtitle')}
                    />
                    {receivedAt && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{t('blog.status.pipeline_date', { date: receivedAt })}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Stale Banner */}
            {isStale && receivedAt && (
                <div
                    data-testid="stale-banner"
                    style={{
                        backgroundColor: 'var(--warning-soft)',
                        borderColor: 'var(--warning-border)',
                        color: 'var(--text)',
                    }}
                    className="p-4 rounded-lg border flex items-center gap-3 text-sm"
                >
                    <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: 'var(--warning)' }} />
                    <span>{t('blog.banner.stale', { time: receivedAt })}</span>
                </div>
            )}

            {/* No Data State */}
            {!loadingOverview && snapshot === null ? (
                <div data-testid="no-data-banner">
                    <EmptyState
                        icon={<Clock className="w-6 h-6" />}
                        title={t('blog.banner.no_data')}
                        description=""
                    />
                </div>
            ) : (
                /* Tabs */
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="published" data-testid="tab-trigger-published">
                            {t('blog.tabs.published')} ({snapshot?.published?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="pending" data-testid="tab-trigger-pending">
                            {t('blog.tabs.pending')} ({snapshot?.pending?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="suggestions" data-testid="tab-trigger-suggestions">
                            {t('blog.tabs.suggestions')}
                        </TabsTrigger>
                    </TabsList>

                    {/* Tab 1: Published */}
                    <TabsContent value="published" className="space-y-4">
                        {/* Filters & Counters */}
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant={publishedFilter === '7d' ? 'default' : 'outline'}
                                    onClick={() => setPublishedFilter('7d')}
                                >
                                    {t('blog.published.filter_7d')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant={publishedFilter === '30d' ? 'default' : 'outline'}
                                    onClick={() => setPublishedFilter('30d')}
                                >
                                    {t('blog.published.filter_30d')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant={publishedFilter === 'all' ? 'default' : 'outline'}
                                    onClick={() => setPublishedFilter('all')}
                                >
                                    {t('blog.published.filter_all')}
                                </Button>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <span>{t('blog.published.count_7d', { n: count7d })}</span>
                                <span>•</span>
                                <span>{t('blog.published.count_total', { n: snapshot?.published?.length || 0 })}</span>
                            </div>
                        </div>

                        {/* Published Table */}
                        <Card>
                            <CardContent className="p-0">
                                {publishedArticles.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        {t('blog.published.empty')}
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-28">{t('blog.published.col_date')}</TableHead>
                                                <TableHead>{t('blog.published.col_title')}</TableHead>
                                                <TableHead className="w-36">{t('blog.published.col_category')}</TableHead>
                                                <TableHead className="w-24">{t('blog.published.col_status')}</TableHead>
                                                <TableHead className="w-24">{t('blog.published.col_links')}</TableHead>
                                                <TableHead className="w-48">{t('blog.published.col_quality')}</TableHead>
                                                <TableHead className="w-20 text-right">{t('blog.card.btn_details')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {publishedArticles.map((item, idx) => {
                                                const q = getQualityStatus(item.quality);
                                                const isCanonical = isCanonicalBlogUrl(item.url_en, item.slug);
                                                return (
                                                    <TableRow key={item.queue_id || idx} data-testid="published-row">
                                                        <TableCell className="font-mono text-xs whitespace-nowrap">
                                                            {item.date}
                                                        </TableCell>
                                                        <TableCell className="font-medium text-foreground">
                                                            {item.title}
                                                        </TableCell>
                                                        <TableCell className="text-muted-foreground text-xs">
                                                            {item.category}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant={item.status === 'published' ? 'default' : 'outline'} data-testid="status-badge">
                                                                {t(`blog.status.${item.status || 'published'}`)}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            {isCanonical ? (
                                                                <a
                                                                    href={item.url_en}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    data-testid="link-en"
                                                                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
                                                                >
                                                                    {t('blog.link.en')}
                                                                    <ExternalLink className="w-3 h-3" />
                                                                </a>
                                                            ) : item.url_en ? (
                                                                <span
                                                                    data-testid="link-text"
                                                                    className="text-xs text-muted-foreground font-mono truncate max-w-[120px] block"
                                                                    title={item.url_en}
                                                                >
                                                                    {item.url_en}
                                                                </span>
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {q.status === 'pass' ? (
                                                                <Badge variant="success" data-testid="quality-badge-pass">
                                                                    <CheckCircle2 className="w-3 h-3 mr-1 inline" />
                                                                    {t('blog.quality.checks_passed', { n: q.passed, total: q.total })}
                                                                </Badge>
                                                            ) : q.status === 'fail' ? (
                                                                <Badge variant="destructive" data-testid="quality-badge-fail" title={q.failedChecks.join(', ')}>
                                                                    <XCircle className="w-3 h-3 mr-1 inline" />
                                                                    {t('blog.quality.checks_failed', { n: q.failed })}
                                                                </Badge>
                                                            ) : q.status === 'partial' ? (
                                                                <Badge variant="secondary" data-testid="quality-badge-partial">
                                                                    {t('blog.quality.partial', { passed: q.passed, total: q.total, not_measured: q.notMeasured })}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="secondary" data-testid="quality-badge-none">
                                                                    {t('blog.quality.not_measured')}
                                                                </Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-7 px-2 text-xs"
                                                                data-testid="btn-view-details"
                                                                onClick={() => setSelectedArticle(item)}
                                                            >
                                                                {t('blog.card.btn_details')}
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab 2: In Queue */}
                    <TabsContent value="pending" className="space-y-4">
                        {/* Queue Banner */}
                        <div
                            data-testid="pending-banner"
                            className="p-4 rounded-lg bg-muted border border-border text-foreground text-sm font-medium flex items-center justify-between"
                        >
                            <span>
                                {pendingArticles.length > 0 && lastExpectedDate
                                    ? t('blog.pending.banner', { n: pendingArticles.length, date: lastExpectedDate })
                                    : t('blog.pending.banner_empty')}
                            </span>
                        </div>

                        {/* Pending Table */}
                        <Card>
                            <CardContent className="p-0">
                                {pendingArticles.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        {t('blog.pending.empty')}
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-12">{t('blog.pending.col_pos')}</TableHead>
                                                <TableHead className="w-36">{t('blog.pending.col_expected')}</TableHead>
                                                <TableHead>{t('blog.pending.col_title')}</TableHead>
                                                <TableHead className="w-36">{t('blog.pending.col_category')}</TableHead>
                                                <TableHead className="w-24">{t('blog.published.col_status')}</TableHead>
                                                <TableHead className="w-36">{t('blog.pending.col_queue_id')}</TableHead>
                                                <TableHead className="w-20 text-right">{t('blog.card.btn_details')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {pendingArticles.map((item, idx) => (
                                                <TableRow key={item.queue_id || idx} data-testid="pending-row">
                                                    <TableCell className="font-mono text-xs text-muted-foreground">
                                                        {idx + 1}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="info" className="whitespace-nowrap">
                                                            <Clock className="w-3 h-3 mr-1 inline" />
                                                            {t('blog.pending.expected_label', { date: item.expected_date })}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="font-medium text-foreground">
                                                        {item.title}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-xs">
                                                        {item.category}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="secondary" data-testid="status-badge">
                                                            {t(`blog.status.${item.status || 'queued'}`)}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="font-mono text-xs text-muted-foreground">
                                                        {item.queue_id}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-7 px-2 text-xs"
                                                            data-testid="btn-view-details"
                                                            onClick={() => setSelectedArticle(item)}
                                                        >
                                                            {t('blog.card.btn_details')}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab 3: Topic Suggestions */}
                    <TabsContent value="suggestions" className="space-y-4">
                        {/* Top bar: Propose Button + Filters */}
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <Button
                                onClick={() => setIsModalOpen(true)}
                                data-testid="btn-propose-topic"
                                className="inline-flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                {t('blog.suggestions.btn_propose')}
                            </Button>

                            <div className="flex items-center gap-4 flex-wrap">
                                {/* Filter: Kind */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">{t('blog.suggestions.filter_kind_label')}:</span>
                                    <select
                                        value={suggestionKindFilter}
                                        onChange={(e) => setSuggestionKindFilter(e.target.value)}
                                        className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                                        data-testid="select-suggestion-kind-filter"
                                    >
                                        <option value="all">{t('blog.suggestions.filter_kind_all')}</option>
                                        <option value="new">{t('blog.suggestions.filter_kind_new')}</option>
                                        <option value="rewrite">{t('blog.suggestions.filter_kind_rewrite')}</option>
                                    </select>
                                </div>

                                {/* Filter: Status */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">{t('blog.suggestions.filter_all')}:</span>
                                    <select
                                        value={suggestionStatusFilter}
                                        onChange={(e) => setSuggestionStatusFilter(e.target.value)}
                                        className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                                        data-testid="select-suggestion-filter"
                                    >
                                        <option value="all">{t('blog.suggestions.filter_all')}</option>
                                        <option value="proposed">{t('blog.suggestions.filter_proposed')}</option>
                                        <option value="accepted">{t('blog.suggestions.filter_accepted')}</option>
                                        <option value="in_queue">{t('blog.suggestions.filter_in_queue')}</option>
                                        <option value="published">{t('blog.suggestions.filter_published')}</option>
                                        <option value="rejected">{t('blog.suggestions.filter_rejected')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Counters Bar: Proposed · Accepted · In queue · Published */}
                        <div
                            className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-muted-foreground font-medium px-1"
                            data-testid="suggestions-counters"
                        >
                            <span data-testid="counter-proposed">
                                {t('blog.suggestions.counter_proposed', { count: suggestionCounters.proposed })}
                            </span>
                            <span>·</span>
                            <span data-testid="counter-accepted">
                                {t('blog.suggestions.counter_accepted', { count: suggestionCounters.accepted })}
                            </span>
                            <span>·</span>
                            <span data-testid="counter-in_queue">
                                {t('blog.suggestions.counter_in_queue', { count: suggestionCounters.in_queue })}
                            </span>
                            <span>·</span>
                            <span data-testid="counter-published">
                                {t('blog.suggestions.counter_published', { count: suggestionCounters.published })}
                            </span>
                        </div>

                        {/* Katifi cycle stats */}
                        {cycleSummary && (
                            <div
                                className="text-xs font-medium text-foreground bg-primary/5 border border-primary/20 rounded-md px-3 py-2 flex items-center gap-2"
                                data-testid="cycle-summary-banner"
                            >
                                <Sparkles className="w-4 h-4 text-primary shrink-0" />
                                <span>{cycleSummary.text}</span>
                            </div>
                        )}

                        {/* Writer notice banner */}
                        <div
                            className="text-xs text-muted-foreground bg-muted/40 border border-border/60 rounded-md px-3 py-2 flex items-center gap-2"
                            data-testid="writer-notice-banner"
                        >
                            <HelpCircle className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span>{t('blog.suggestions.writer_notice')}</span>
                        </div>

                        {/* Suggestions Table */}
                        <Card>
                            <CardContent className="p-0">
                                {loadingSuggestions ? (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        ...
                                    </div>
                                ) : sortedSuggestions.length === 0 ? (
                                    <div className="p-8 text-center text-muted-foreground text-sm">
                                        {t('blog.suggestions.empty')}
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('blog.suggestions.col_title')}</TableHead>
                                                <TableHead className="w-36">{t('blog.suggestions.col_keyword')}</TableHead>
                                                <TableHead className="w-24">{t('blog.suggestions.col_priority')}</TableHead>
                                                <TableHead className="w-24">{t('blog.suggestions.col_language')}</TableHead>
                                                <TableHead className="w-32">{t('blog.suggestions.col_status')}</TableHead>
                                                <TableHead className="w-36">{t('blog.suggestions.col_queue_id')}</TableHead>
                                                <TableHead className="w-36 text-right">{t('blog.suggestions.col_actions')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {sortedSuggestions.map((s) => (
                                                <TableRow key={s.id} data-testid="suggestion-row">
                                                    <TableCell>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-medium text-foreground">{s.title}</span>
                                                            <Badge
                                                                variant={s.kind === 'rewrite' ? 'secondary' : 'outline'}
                                                                className="text-[10px] px-1.5 py-0 h-4 uppercase tracking-wider font-semibold"
                                                                data-testid={`badge-kind-${s.id}`}
                                                            >
                                                                {s.kind === 'rewrite' ? t('blog.suggestions.kind_rewrite') : t('blog.suggestions.kind_new')}
                                                            </Badge>
                                                        </div>

                                                        {/* Category · Proposed by Katifi · Oct 2026 */}
                                                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                                                            {s.category && <span>{s.category}</span>}
                                                            {s.category && (s.proposed_by || s.cycle) && <span>·</span>}

                                                            {s.proposed_by === 'katifi' ? (
                                                                <span
                                                                    title={s.engine ? t('blog.suggestions.generated_with', { engine: s.engine }) : undefined}
                                                                    className={s.engine ? 'cursor-help border-b border-dotted border-muted-foreground/50' : undefined}
                                                                    data-testid={`katifi-author-${s.id}`}
                                                                >
                                                                    {t('blog.suggestions.proposed_by_katifi', { name: t('blog.assistant.katifi') })}
                                                                </span>
                                                            ) : s.proposed_by === 'staff' ? (
                                                                <span data-testid={`staff-author-${s.id}`}>{t('blog.suggestions.proposed_by_staff')}</span>
                                                            ) : s.proposed_by === 'owner' ? (
                                                                <span data-testid={`owner-author-${s.id}`}>{t('blog.suggestions.proposed_by_owner')}</span>
                                                            ) : s.proposed_by ? (
                                                                <span>{s.proposed_by}</span>
                                                            ) : null}

                                                            {s.cycle && (s.category || s.proposed_by) && <span>·</span>}
                                                            {s.cycle && <span>{formatCycleMonth(s.cycle, lang)}</span>}
                                                        </div>

                                                        {/* Rewrite target link if kind === 'rewrite' and target_slug */}
                                                        {s.kind === 'rewrite' && s.target_slug && (
                                                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1 font-mono">
                                                                <span className="font-sans text-xs">{t('blog.suggestions.target_article')}:</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setActiveTab('published');
                                                                        const matched = (snapshot?.published || []).find(
                                                                            (p) => p.slug === s.target_slug || p.queue_id === s.target_slug
                                                                        );
                                                                        if (matched) setSelectedArticle(matched);
                                                                    }}
                                                                    data-testid={`link-topic-rewrite-${s.id}`}
                                                                    className="text-primary hover:underline inline-flex items-center gap-1 cursor-pointer"
                                                                >
                                                                    <span>{s.target_slug}</span>
                                                                    <ExternalLink className="w-3 h-3 shrink-0" />
                                                                </button>
                                                            </div>
                                                        )}

                                                        {/* Rationale if present */}
                                                        {s.rationale && (
                                                            <div className="text-xs text-muted-foreground/90 mt-1 italic">
                                                                {s.rationale}
                                                            </div>
                                                        )}

                                                        {/* Notes if present */}
                                                        {s.notes && (
                                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                                {s.notes}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-muted-foreground">
                                                        {s.keyword || '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {s.priority === 'high' ? (
                                                            <Badge variant="destructive">{t('blog.priority.high')}</Badge>
                                                        ) : s.priority === 'low' ? (
                                                            <Badge variant="outline">{t('blog.priority.low')}</Badge>
                                                        ) : (
                                                            <Badge variant="secondary">{t('blog.priority.normal')}</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">
                                                            {s.language === 'both'
                                                                ? t('blog.language.both')
                                                                : s.language === 'ru'
                                                                ? t('blog.language.ru')
                                                                : t('blog.language.en')}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            variant={
                                                                s.status === 'published'
                                                                    ? 'default'
                                                                    : s.status === 'in_queue'
                                                                    ? 'secondary'
                                                                    : s.status === 'rejected'
                                                                    ? 'outline'
                                                                    : 'outline'
                                                            }
                                                            title={s.status === 'accepted' ? t('blog.status.accepted_tooltip') : undefined}
                                                        >
                                                            {s.status === 'published'
                                                                ? t('blog.status.published')
                                                                : s.status === 'in_queue'
                                                                ? t('blog.status.in_queue')
                                                                : s.status === 'accepted'
                                                                ? t('blog.status.accepted')
                                                                : s.status === 'rejected'
                                                                ? t('blog.status.rejected')
                                                                : t('blog.status.proposed')}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="font-mono text-xs">
                                                        {s.status === 'in_queue' && s.queue_id ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setActiveTab('pending');
                                                                    const matched = (snapshot?.pending || []).find(
                                                                        (p) => p.queue_id === s.queue_id || p.slug === s.queue_id
                                                                    );
                                                                    if (matched) setSelectedArticle(matched);
                                                                }}
                                                                data-testid={`link-topic-article-${s.id}`}
                                                                className="text-primary hover:underline font-mono inline-flex items-center gap-1 text-left cursor-pointer"
                                                                title={t('blog.suggestions.view_in_queue')}
                                                            >
                                                                <span>{s.queue_id}</span>
                                                                <ExternalLink className="w-3 h-3 shrink-0" />
                                                            </button>
                                                        ) : s.status === 'published' && s.queue_id ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setActiveTab('published');
                                                                    const matched = (snapshot?.published || []).find(
                                                                        (p) => p.queue_id === s.queue_id || p.slug === s.queue_id
                                                                    );
                                                                    if (matched) setSelectedArticle(matched);
                                                                }}
                                                                data-testid={`link-topic-article-${s.id}`}
                                                                className="text-primary hover:underline font-mono inline-flex items-center gap-1 text-left cursor-pointer"
                                                                title={t('blog.suggestions.view_in_published')}
                                                            >
                                                                <span>{s.queue_id}</span>
                                                                <ExternalLink className="w-3 h-3 shrink-0" />
                                                            </button>
                                                        ) : (
                                                            <span className="text-muted-foreground">{s.queue_id || '—'}</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            {s.status === 'proposed' && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="h-7 px-2"
                                                                    style={{ color: 'var(--success)' }}
                                                                    onClick={() => handleStatusChange(s.id, 'accepted')}
                                                                >
                                                                    {t('blog.action.accept')}
                                                                </Button>
                                                            )}
                                                            {s.status !== 'rejected' && s.status !== 'published' && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="h-7 px-2"
                                                                    style={{ color: 'var(--danger)' }}
                                                                    onClick={() => handleStatusChange(s.id, 'rejected')}
                                                                >
                                                                    {t('blog.action.reject')}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            )}

            {/* Modal: Suggest Topic */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('blog.modal.title')}</DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmitSuggestion} className="space-y-4 py-2" data-testid="form-suggest-topic">
                        {modalError && (
                            <div className="p-3 text-xs rounded bg-destructive/10 text-destructive">
                                {modalError}
                            </div>
                        )}

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-foreground">
                                {t('blog.modal.field_title')}
                            </label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="e.g. Probate Process in New York: Step-by-Step"
                                required
                                data-testid="input-modal-title"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-foreground">
                                    {t('blog.modal.field_keyword')}
                                </label>
                                <Input
                                    value={formData.keyword}
                                    onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                                    placeholder="e.g. probate NY"
                                    data-testid="input-modal-keyword"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-foreground">
                                    {t('blog.modal.field_language')}
                                </label>
                                <select
                                    value={formData.language}
                                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                                    data-testid="select-modal-language"
                                >
                                    <option value="en">{t('blog.language.en')}</option>
                                    <option value="both">{t('blog.language.both')}</option>
                                    <option value="ru">{t('blog.language.ru')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-foreground">
                                    {t('blog.modal.field_priority')}
                                </label>
                                <select
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                                    data-testid="select-modal-priority"
                                >
                                    <option value="high">{t('blog.priority.high')}</option>
                                    <option value="normal">{t('blog.priority.normal')}</option>
                                    <option value="low">{t('blog.priority.low')}</option>
                                </select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-foreground">
                                    {t('blog.modal.field_queue_id')}
                                </label>
                                <Input
                                    value={formData.queue_id}
                                    onChange={(e) => setFormData({ ...formData, queue_id: e.target.value })}
                                    placeholder="e.g. eln-2026-10-26-01"
                                    data-testid="input-modal-queue-id"
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-foreground">
                                {t('blog.modal.field_notes')}
                            </label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Additional details or angle for the article..."
                                rows={3}
                                data-testid="input-modal-notes"
                            />
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsModalOpen(false)}
                                disabled={submittingModal}
                            >
                                {t('blog.modal.btn_cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={submittingModal || !formData.title.trim()}
                                data-testid="btn-modal-submit"
                            >
                                {submittingModal ? t('blog.modal.submitting') : t('blog.modal.btn_submit')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modal: Article Details Card */}
            <Dialog open={!!selectedArticle} onOpenChange={(open) => !open && setSelectedArticle(null)}>
                <DialogContent
                    className="max-w-2xl max-h-[85vh] overflow-y-auto"
                    style={{ maxWidth: '42rem', maxHeight: '85vh' }}
                    data-testid="dialog-article-details"
                >
                    <DialogHeader>
                        <DialogTitle className="text-lg font-bold">
                            {selectedArticle?.title}
                        </DialogTitle>
                    </DialogHeader>

                    {selectedArticle && (() => {
                        const q = getQualityStatus(selectedArticle.quality);
                        const isCanonical = isCanonicalBlogUrl(selectedArticle.url_en, selectedArticle.slug);
                        const fc = selectedArticle.fact_check;
                        const isBrowserVerbatim = fc && fc.method === 'browser-verbatim';

                        return (
                            <div className="space-y-4 py-2 text-sm">
                                {/* Basic Meta */}
                                <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-muted/40 border border-border">
                                    <div>
                                        <div className="text-xs text-muted-foreground">{t('blog.card.slug')}</div>
                                        <div className="font-mono text-xs font-semibold">{selectedArticle.slug}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground">{t('blog.card.category')}</div>
                                        <div className="font-medium text-xs">{selectedArticle.category}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground">{t('blog.card.status')}</div>
                                        <Badge variant="outline" className="text-xs mt-0.5">
                                            {t(`blog.status.${selectedArticle.status || 'published'}`)}
                                        </Badge>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground">{t('blog.card.date')}</div>
                                        <div className="font-mono text-xs">
                                            {selectedArticle.published_at || selectedArticle.date || selectedArticle.expected_date || '—'}
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="text-xs text-muted-foreground">{t('blog.card.url')}</div>
                                        {isCanonical ? (
                                            <a
                                                href={selectedArticle.url_en}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="card-canonical-link"
                                                className="text-primary hover:underline font-mono text-xs inline-flex items-center gap-1 break-all"
                                            >
                                                {selectedArticle.url_en}
                                                <ExternalLink className="w-3 h-3 shrink-0" />
                                            </a>
                                        ) : selectedArticle.url_en ? (
                                            <span data-testid="card-link-text" className="font-mono text-xs text-muted-foreground break-all">
                                                {selectedArticle.url_en}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">—</span>
                                        )}
                                    </div>
                                </div>

                                {/* Fact Check Block */}
                                <div className="border border-border rounded-lg p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                            <ShieldCheck className="w-4 h-4 text-primary" />
                                            {t('blog.card.fact_check_title')}
                                        </h4>
                                        {fc ? (
                                            <Badge variant={isBrowserVerbatim ? 'success' : 'destructive'} className="text-xs">
                                                {fc.method}
                                            </Badge>
                                        ) : (
                                            <Badge variant="secondary" className="text-xs" data-testid="card-fact-check-none">
                                                {t('blog.card.fact_check_not_measured')}
                                            </Badge>
                                        )}
                                    </div>

                                    {!fc ? (
                                        <div className="text-xs text-muted-foreground italic" data-testid="fact-check-not-measured">
                                            {t('blog.card.fact_check_not_measured')}
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {!isBrowserVerbatim && (
                                                <div
                                                    data-testid="fact-check-warning"
                                                    className="p-2.5 rounded bg-destructive/10 border border-destructive/20 text-destructive text-xs font-semibold flex items-center gap-2"
                                                >
                                                    <ShieldAlert className="w-4 h-4 shrink-0" />
                                                    <span>{t('blog.card.fact_check_warning')}</span>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                                <div className="p-2 rounded bg-muted/30">
                                                    <span className="text-muted-foreground block">{t('blog.card.fact_check_claims')}</span>
                                                    <span className="font-semibold">{fc.claims_total ?? '—'}</span>
                                                </div>
                                                <div className="p-2 rounded bg-muted/30">
                                                    <span className="text-muted-foreground block">{t('blog.card.fact_check_exact')}</span>
                                                    <span className="font-semibold text-success">{fc.exact ?? '—'}</span>
                                                </div>
                                                <div className="p-2 rounded bg-muted/30">
                                                    <span className="text-muted-foreground block">{t('blog.card.fact_check_fixed')}</span>
                                                    <span className="font-semibold">{fc.fixed ?? '—'}</span>
                                                </div>
                                                <div className="p-2 rounded bg-muted/30">
                                                    <span className="text-muted-foreground block">{t('blog.card.fact_check_open')}</span>
                                                    <span className={`font-semibold ${fc.open > 0 ? 'text-destructive' : ''}`}>{fc.open ?? '—'}</span>
                                                </div>
                                            </div>
                                            {fc.checked_at && (
                                                <div className="text-xs text-muted-foreground">
                                                    {t('blog.card.fact_check_checked_at')}: <span className="font-mono">{fc.checked_at}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Alan Block */}
                                <div className="border border-border rounded-lg p-3 space-y-1">
                                    <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                        <Mail className="w-4 h-4 text-primary" />
                                        {t('blog.card.alan_title')}
                                    </h4>
                                    {selectedArticle.alan_status === 'sent' || (selectedArticle.alan_emailed_at && selectedArticle.alan_status !== 'not_sent') ? (
                                        <div className="text-xs" data-testid="alan-emailed-at">
                                            {t('blog.card.alan_sent_at')}: <span className="font-mono font-medium">{selectedArticle.alan_emailed_at || '—'}</span>
                                        </div>
                                    ) : selectedArticle.alan_status === 'not_sent' ? (
                                        <div className="text-xs text-destructive font-semibold" data-testid="alan-not-sent">
                                            {t('blog.card.alan_not_sent')}
                                        </div>
                                    ) : (
                                        <div className="text-xs text-muted-foreground italic" data-testid="alan-no-record">
                                            {t('blog.card.alan_no_record')}
                                        </div>
                                    )}
                                </div>

                                {/* Quality Block */}
                                <div className="border border-border rounded-lg p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                            <CheckCircle2 className="w-4 h-4 text-primary" />
                                            {t('blog.card.quality_title')}
                                        </h4>
                                        {q.status === 'none' ? (
                                            <Badge variant="secondary" className="text-xs" data-testid="card-quality-none">
                                                {t('blog.card.quality_not_measured')}
                                            </Badge>
                                        ) : q.status === 'pass' ? (
                                            <Badge variant="success" className="text-xs" data-testid="card-quality-pass">
                                                {t('blog.quality.checks_passed', { n: q.passed, total: q.total })}
                                            </Badge>
                                        ) : q.status === 'partial' ? (
                                            <Badge variant="secondary" className="text-xs" data-testid="card-quality-partial">
                                                {t('blog.quality.partial', { passed: q.passed, total: q.total, not_measured: q.notMeasured })}
                                            </Badge>
                                        ) : (
                                            <Badge variant="destructive" className="text-xs" data-testid="card-quality-fail">
                                                {t('blog.quality.checks_failed', { n: q.failedChecks.length })}
                                            </Badge>
                                        )}
                                    </div>

                                    {q.status === 'none' ? (
                                        <div className="text-xs text-muted-foreground italic" data-testid="quality-not-measured">
                                            {t('blog.card.quality_not_measured')}
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5 pt-1">
                                            {Object.entries(q.checks).map(([key, val]) => {
                                                const isPass = val.passed === true || val.status === 'pass';
                                                const isNotMeasured = val.passed === null || val.status === 'not_measured';
                                                const checkLabelKey = `blog.quality.check.${key}`;
                                                const translatedTitle = t(checkLabelKey) !== checkLabelKey ? t(checkLabelKey) : key;
                                                return (
                                                    <div
                                                        key={key}
                                                        className="flex items-start justify-between gap-2 p-1.5 rounded bg-muted/20 text-xs"
                                                    >
                                                        <div className="flex items-center gap-1.5 font-medium">
                                                            {isPass ? (
                                                                <CheckCircle2 className="w-3.5 h-3.5 text-success shrink-0" />
                                                            ) : isNotMeasured ? (
                                                                <HelpCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                                            ) : (
                                                                <XCircle className="w-3.5 h-3.5 text-destructive shrink-0" />
                                                            )}
                                                            <span>{translatedTitle}</span>
                                                        </div>
                                                        <div className="text-right text-muted-foreground text-[11px] max-w-[65%]">
                                                            {val.message || val.reason || (isPass ? 'pass' : isNotMeasured ? t('blog.card.quality_not_measured') : 'fail')}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSelectedArticle(null)}
                            data-testid="btn-close-details"
                        >
                            {t('blog.card.btn_close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
